import { GsbEntityService } from '../../services/entity/gsb-entity.service';
import { GsbSaveRequest, GsbUploadFileRequest } from '../../types/requests';
import { QueryParams } from '../../types/query-params';
import { QueryFunction } from '../../types/query';
import { getGsbToken, getGsbTenantCode } from '../../config/gsb-config';
import { GsbFile, FileType, ListingType } from '../../models/gsb-file.model';
import {  getGsbDateSortCols } from '../../utils/gsb-utils';

export const DEFAULT_FOLDER_TYPE = FileType.Cloud_AWS;
/**
 * Service for managing Files and Storage in the application
 */
export class GsbFileService {
  private static    instance: GsbFileService;
  private entityService: GsbEntityService;
  private ENTITY_NAME = 'GsbFile';

  constructor() {
    this.entityService = GsbEntityService.getInstance();
  }

  static getInstance(): GsbFileService {
    if (!GsbFileService.instance) {
      GsbFileService.instance = new GsbFileService();
    }
    return GsbFileService.instance;
  }

  
  /**
   * Get file by ID
   * @param id The file ID
   * @returns The file or null if not found
   */
  async getFileById(id: string): Promise<GsbFile | null> {
    try {
      const file = await this.entityService.getById<GsbFile>(
        this.ENTITY_NAME,
        id,
        getGsbToken(),
        getGsbTenantCode()
      );
      return file;
    } catch (error) {
      console.error('Error getting file by ID:', error);
      return null;
    }
  }

  /**
   * Get files in a folder
   * @param folderId The parent folder ID (null or undefined for root)
   * @param page The page number (starting from 1)
   * @param pageSize The number of items per page
   * @returns An array of files and the total count
   */
  async getFiles(
    folderId?: string,
    page: number = 1,
    pageSize: number = 10
  ): Promise<{ files: GsbFile[]; totalCount: number }> {
    try {
      const query :any = new QueryParams<GsbFile>(this.ENTITY_NAME);

      // Filter by parent folder
      if (folderId) {
        query.filters = [
          {
            propVal: {
              name: 'parent_id',
              value: folderId
            },
            function: QueryFunction.Equals
          }
        ];
      } else {
        // For root, parent_id should be null or undefined
        query.filters = [
          {
            propVal: {
              name: 'parent_id',
              value: null
            },
            function: QueryFunction.Is
          }
        ];
      }

      // Set pagination
      query.startIndex = (page - 1) * pageSize;
      query.count = pageSize;
      query.calcTotalCount = true;

      // Sort by creation date
      query.sortCols = getGsbDateSortCols();

      const response = await this.entityService.query(
        query,
        getGsbToken(),
        getGsbTenantCode()
      );

      return {
        files: (response.entities || []) as GsbFile[],
        totalCount: response.totalCount || 0
      };
    } catch (error) {
      console.error('Error getting files:', error);
      return { files: [], totalCount: 0 };
    }
  }

  /**
   * Search for files
   * @param searchTerm The search term
   * @param page The page number (starting from 1)
   * @param pageSize The number of items per page
   * @returns An array of files and the total count
   */
  async searchFiles(
    searchTerm: string,
    page: number = 1,
    pageSize: number = 10
  ): Promise<{ files: GsbFile[]; totalCount: number }> {
    try {
      const query = new QueryParams<GsbFile>(this.ENTITY_NAME);

      // Set search filter
      if (searchTerm) {
        query.searchText = searchTerm;
      }

      // Set pagination
      query.startIndex = (page - 1) * pageSize;
      query.count = pageSize;
      query.calcTotalCount = true;

      const response = await this.entityService.query(
        query,
        getGsbToken(),
        getGsbTenantCode()
      );

      return {
        files: (response.entities || []) as GsbFile[],
        totalCount: response.totalCount || 0
      };
    } catch (error) {
      console.error('Error searching files:', error);
      return { files: [], totalCount: 0 };
    }
  }

  /**
   * Create a new folder
   * @param name The folder name
   * @param parentId The parent folder ID (optional)
   * @returns The created folder ID or null if failed
   */
  async createFolder(name: string, parentId?: string, parentPath?: string): Promise<string | null> {
    try {
      // Set defaults
      const newFolder: GsbFile = {
        name,
        fileType: DEFAULT_FOLDER_TYPE,
        listingType: ListingType.Folder,
        parent_id: parentId || '',
        path: parentPath ? `${parentPath}/${name}` : undefined
      };

      // Set create date fields

      const request: GsbSaveRequest = {
        entDefName: this.ENTITY_NAME,
            entity: newFolder,
        entityDef: {},
        entityId: '',
        entDefId: '',
        filters: []
      };

      const response = await this.entityService.save(
        request,
        getGsbToken(),
        getGsbTenantCode()
      );

      if (!response.id) {
        return null;
      }

      return response.id;
    } catch (error) {
      console.error('Error creating folder:', error);
      return null;
    }
  }

  /**
   * Upload a file
   * @param file The file to upload
   * @param name The file name (optional, defaults to file.name)
   * @param parentId The parent folder ID (optional)
   * @returns The uploaded file ID or null if failed
   */
  async uploadFile(file: File, entity: GsbFile): Promise<GsbFile | null> {
    try {
      // Create file metadata
      const newFile: GsbFile = {
        name: entity.name || file.name,
        fileType: DEFAULT_FOLDER_TYPE,
        listingType: ListingType.File,
        parent_id: entity.parent_id ,
        size: file.size,
        contentType: file.type,
        path: entity.path 
      };


      const request: GsbUploadFileRequest = {
        file: file,
        entity: newFile
      };

      const response = await this.entityService.uploadFile(
        request,
        getGsbToken(),
        getGsbTenantCode()
      );


      return response?.fileDto || null;
    } catch (error) {
      console.error('Error uploading file:', error);
      return null;
    }
  }

  /**
   * Delete a file or folder
   * @param id The file or folder ID to delete
   * @returns True if deleted successfully, false otherwise
   */
  async deleteFile(id: string): Promise<boolean> {
    try {
      const request: GsbSaveRequest = {
        entDefName: this.ENTITY_NAME,
        entityId: id
      };

      const response = await this.entityService.delete(
        request,
        getGsbToken(),
        getGsbTenantCode()
      );

      return response.deleteCount === 1;
    } catch (error) {
      console.error('Error deleting file:', error);
      return false;
    }
  }

  /**
   * Get file content
   * @param id The file ID
   * @returns The file content or null if failed
   */
  async getFileContent(id: string): Promise<Blob | null> {
    try {
      const file = await this.getFileById(id);
      if (!file) {
        throw new Error('File not found');
      }

      if (file.listingType !== ListingType.File) {
        throw new Error('Not a file');
      }

      if (!file.publicUrl) {
        throw new Error('File has no public URL');
      }

      // Fetch the file content from the public URL
      const response = await fetch(file.publicUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch file content: ${response.statusText}`);
      }

      return await response.blob();
    } catch (error) {
      console.error('Error getting file content:', error);
      return null;
    }
  }
}
